import React, { useState, useRef } from 'react';
import { useToast } from '../../../components/ui/Toast';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FileUploadZone = ({ mainFile, supplementaryFiles, onMainFileChange, onSupplementaryFilesChange, error, allowedFileTypes = 'both' }) => {
  // Replaces toastError(): non-blocking and styled; errors persist until
  // dismissed rather than vanishing on a timer.
  const { error: toastError } = useToast();
  const { t } = useTranslation('submissions');
  const k = (key, opts) => t(`authorPortal.upload.${key}`, opts);

  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const mainFileInputRef = useRef(null);
  const suppFileInputRef = useRef(null);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const kb = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(kb));
    return Math.round(bytes / Math.pow(kb, i) * 100) / 100 + ' ' + sizes?.[i];
  };

  const fileTypeConfig = {
    pdf: {
      mimeTypes: ['application/pdf'],
      accept: '.pdf',
      label: k('formats.pdf')
    },
    docx: {
      mimeTypes: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
      accept: '.docx',
      label: k('formats.docx')
    },
    both: {
      mimeTypes: ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
      accept: '.pdf,.docx',
      label: k('formats.both')
    }
  };

  const normalizedAllowedFileTypes = fileTypeConfig?.[allowedFileTypes] ? allowedFileTypes : 'both';
  const currentConfig = fileTypeConfig?.[normalizedAllowedFileTypes];

  const validateFile = (file) => {
    const maxSize = 100 * 1024 * 1024; // 100MB

    if (file?.size > maxSize) {
      return k('errors.tooLarge');
    }

    if (!currentConfig?.mimeTypes?.includes(file?.type)) {
      return k('errors.wrongType', { formats: currentConfig?.label });
    }

    return null;
  };

  const simulateUpload = (file, callback) => {
    setIsUploading(true);
    setUploadProgress(0);

    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsUploading(false);
          callback(file);
          return 100;
        }
        return prev + 10;
      });
    }, 200);
  };

  const handleMainFileDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);

    const file = e?.dataTransfer?.files?.[0];
    if (file) {
      const validationError = validateFile(file);
      if (validationError) {
        toastError(validationError);
        return;
      }
      simulateUpload(file, onMainFileChange);
    }
  };

  const handleMainFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      const validationError = validateFile(file);
      if (validationError) {
        toastError(validationError);
        return;
      }
      simulateUpload(file, onMainFileChange);
    }
  };

  const handleSupplementaryFileSelect = (e) => {
    const files = Array.from(e?.target?.files);
    const validFiles = [];

    for (const file of files) {
      const validationError = validateFile(file);
      if (validationError) {
        toastError(`${file?.name}: ${validationError}`);
        continue;
      }
      validFiles?.push(file);
    }

    if (validFiles?.length > 0) {
      onSupplementaryFilesChange([...supplementaryFiles, ...validFiles]);
    }
  };

  const removeSupplementaryFile = (index) => {
    onSupplementaryFilesChange(supplementaryFiles?.filter((_, i) => i !== index));
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragging(false);
  };

  return (
    <div className="bg-card rounded-lg p-4 md:p-6 lg:p-8 shadow-elevation-2">
      <div className="flex items-start gap-3 mb-4 md:mb-6">
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
          <Icon name="Upload" size={20} className="text-accent" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground mb-1">{k('title')}</h2>
          <p className="text-sm md:text-base text-muted-foreground">{k('subtitle')}</p>
        </div>
      </div>
      <div className="space-y-4 md:space-y-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            {k('mainFileLabel')} <span className="text-error">*</span>
          </label>
          <div
            className={`border-2 border-dashed rounded-lg p-6 md:p-8 text-center transition-250 ${
              isDragging ? 'border-primary bg-primary/5' : 'border-border'
            }`}
            onDrop={handleMainFileDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            {!mainFile && !isUploading && (
              <>
                <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 bg-muted flex items-center justify-center">
                  <Icon name="FileUp" size={24} className="text-muted-foreground" />
                </div>
                <p className="text-sm md:text-base text-foreground mb-2">
                  {k('dropText')}
                </p>
                <p className="text-xs md:text-sm text-muted-foreground mb-4">
                  {k('sizeHint', { formats: currentConfig?.label })}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="default"
                  onClick={() => mainFileInputRef?.current?.click()}
                  iconName="Upload"
                  iconPosition="left"
                >
                  {k('choose')}
                </Button>
                <input
                  ref={mainFileInputRef}
                  type="file"
                  accept={currentConfig?.accept}
                  onChange={handleMainFileSelect}
                  className="hidden"
                />
              </>
            )}

            {isUploading && (
              <div className="space-y-3">
                <div className="w-12 h-12 md:w-16 md:h-16 mx-auto bg-primary/10 flex items-center justify-center">
                  <Icon name="Loader2" size={24} className="text-primary animate-spin" />
                </div>
                <p className="text-sm md:text-base text-foreground">{k('uploading')}</p>
                <div className="max-w-xs mx-auto">
                  <div className="h-2 bg-muted overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-250"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-xs md:text-sm text-muted-foreground mt-2">{uploadProgress}%</p>
                </div>
              </div>
            )}

            {mainFile && !isUploading && (
              <div className="flex items-center gap-3 p-3 md:p-4 bg-muted rounded-lg">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="FileText" size={20} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0 text-start">
                  <p className="text-sm md:text-base font-medium text-foreground truncate">{mainFile?.name}</p>
                  <p className="text-xs md:text-sm text-muted-foreground">{formatFileSize(mainFile?.size)}</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => onMainFileChange(null)}
                  iconName="X"
                />
              </div>
            )}
          </div>
          {error && (
            <p className="mt-2 text-xs md:text-sm text-error">{error}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            {k('supplementaryLabel')}
          </label>
          <p className="text-xs md:text-sm text-muted-foreground mb-3">
            {k('supplementaryHint')}
          </p>

          {supplementaryFiles?.length > 0 && (
            <div className="space-y-2 mb-4">
              {supplementaryFiles?.map((file, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                  <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="Paperclip" size={16} className="text-secondary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm md:text-base font-medium text-foreground truncate">{file?.name}</p>
                    <p className="text-xs md:text-sm text-muted-foreground">{formatFileSize(file?.size)}</p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeSupplementaryFile(index)}
                    iconName="Trash2"
                  />
                </div>
              ))}
            </div>
          )}

          <Button
            type="button"
            variant="outline"
            size="default"
            onClick={() => suppFileInputRef?.current?.click()}
            iconName="Plus"
            iconPosition="left"
            fullWidth
          >
            {k('addSupplementary')}
          </Button>
          <input
            ref={suppFileInputRef}
            type="file"
            accept={currentConfig?.accept}
            multiple
            onChange={handleSupplementaryFileSelect}
            className="hidden"
          />
        </div>

        <div className="bg-muted/50 rounded-lg p-4 md:p-6">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm md:text-base font-medium text-foreground mb-2">{k('guidelines.title')}</h3>
              <ul className="space-y-1 text-xs md:text-sm text-muted-foreground">
                <li>• {k('guidelines.template')}</li>
                <li>• {k('guidelines.maxSize')}</li>
                <li>• {k('guidelines.accepted', { formats: currentConfig?.label })}</li>
                <li>• {k('guidelines.fonts')}</li>
                <li>• {k('guidelines.blind')}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileUploadZone;
